import React, { useState, useEffect } from 'react'

import { connect } from 'containers/store'

import { ButtonGroup, Button } from 'components/ui/button'
import { Description, Title, ListItemLabel } from 'components/ui/list-item-label'
import { Modal } from 'components/ui/modal'
import { RadioInputElement } from 'components/ui/radio'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { GettyAppState, GettyStore } from 'domains/org/getty/store'

import { CarouselItemValue, Stop } from 'generated/mos/org/getty/audio'
import { CreateCarouselItemRequest } from 'generated/mos/org/getty/audiomanagement'

import { pick } from 'helpers/core'
import { Status } from 'helpers/status'

type ActionProps = Pick<GettyActionCreators, 'gettyCreateCarouselItemRequest' | 'gettyUpdateStopReset'>
type ConnectedProps = Pick<GettyAppState, 'carouselItemCreate' | 'carouselItemUpdate'>;

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly onCancel: () => void;
  readonly projectId: string;
  readonly stop: Stop.Entity;
  readonly stopId: string;
  readonly tourId?: string;
};
type Props = ActionProps & ConnectedProps & DirectProps;

export const CarouselItemCreateView = (props: Props) => {
  const {
    carouselItemCreate,
    carouselItemUpdate,
    gettyCreateCarouselItemRequest,
    gettyUpdateStopReset,
    historyPush,
    onCancel,
    projectId,
    stop,
    stopId,
    tourId,
  } = props

  // where to navigate to after the carousel item is successfully created
  // consider whether we got here from within a tour or not
  const redirectLocation = tourId ? `/project/${projectId}/tour/${tourId}/stop/${stopId}` : `/project/${projectId}/stop/${stopId}`
  const options = ['Image', 'Audio', 'Video'] as const

  const [ selectedOption, setSelectedOption ] = useState<typeof options[number] | undefined>(undefined)

  const onCreatingNewSlide = () => {
    let itemValue;
    switch(selectedOption) {
      case 'Image': {
        itemValue = CarouselItemValue.ImageItem.defaults
        break
      }
      case 'Audio': {
        itemValue = CarouselItemValue.AudioItem.defaults
        break
      }
      case 'Video': {
        itemValue = CarouselItemValue.VideoItem.defaults
        break
      }
    }

    gettyCreateCarouselItemRequest({
      ...CreateCarouselItemRequest.defaults,
      value:{
        type: CarouselItemValue.refName,
        item: itemValue
      }
    }, stop)
  }

  useEffect(() =>{
    if (carouselItemCreate.status === Status.Complete &&
      carouselItemUpdate.status === Status.Ready &&
      carouselItemUpdate.data.ref) {

        gettyUpdateStopReset()

        //navigate to edit slide
        historyPush(`${redirectLocation}/media-carousel/${carouselItemUpdate.data.ref.id}`)
    }

  }, [carouselItemCreate.status]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      header={<h3>Create a new slide</h3>}
      onClose={onCancel}
      footer={(
        <ButtonGroup>
          <Button onClick={onCancel} appearance="secondary">Cancel</Button>
          <Button
            onClick={onCreatingNewSlide}
            isDisabled={!selectedOption}
          >
            Create
          </Button>
        </ButtonGroup>
      )}
    >
      {options.map(option => (
        <ListItemLabel key={option}>
          <RadioInputElement
            checked={selectedOption === option}
            onChange={() => setSelectedOption(option)}
            name={option}
          />
          <Description>
            <Title>{option}</Title>
          </Description>
        </ListItemLabel>
      ))}
    </Modal>
  )
}

export const CarouselItemCreate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => pick(store.getty, 'carouselItemCreate', 'carouselItemUpdate'),
  pick(
    actionCreators, 'gettyCreateCarouselItemRequest', 'gettyUpdateStopRequest', 'gettyUpdateStopReset',
  ),
)(CarouselItemCreateView);
